// ========================================
// pages/NextOfKin.js - Global Next of Kin Management
// ========================================

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from '../utils/axiosConfig';

const NextOfKin = () => {
  const navigate = useNavigate();
  const [noks, setNoks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [userPlan, setUserPlan] = useState(null);
  const [planLimits, setPlanLimits] = useState({ min: 2, max: 2 });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    relationship: 'other'
  });
  
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchUserAndNoks();
  }, []);

  // ========================================
  // FETCH USER DATA AND NOK LIMITS
  // ========================================
  const fetchUserAndNoks = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const userResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const user = userResponse.data.user;
      setUserPlan(user.plan);
      
      // ========================================
      // CRITICAL: Get the max NOK limit from the user's plan
      // The backend sends 'limits' object from PlanConfig database
      // ========================================
      const maxGlobalNoks = user.limits?.globalNoks || 2;
      
      setPlanLimits({
        min: 2,  // Minimum is always 2 for all plans
        max: maxGlobalNoks,
        current: user.globalNextOfKin?.length || 0
      });
      
      setNoks(user.globalNextOfKin || []);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      // ========================================
      // Check if user has reached the limit
      // ========================================
      if (noks.length >= planLimits.max) {
        setError(`Your ${userPlan} plan allows maximum ${planLimits.max} Global Next of Kin.`);
        setSubmitting(false);
        return;
      }

      await axios.post(`${process.env.REACT_APP_API_URL}/api/users/nok`, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        relationship: 'other'
      });
      setShowForm(false);
      fetchUserAndNoks();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add Next of Kin');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (nokId) => {
    if (!window.confirm('Are you sure you want to remove this Next of Kin?')) return;

    try {
      const token = localStorage.getItem('token');
      
      if (noks.length - 1 < planLimits.min) {
        setError(`Your ${userPlan} plan requires minimum ${planLimits.min} Global Next of Kin.`);
        return;
      }

      await axios.delete(`${process.env.REACT_APP_API_URL}/api/users/nok/${nokId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      fetchUserAndNoks();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete Next of Kin');
    }
  };

  const handleResendInvite = async (nokId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${process.env.REACT_APP_API_URL}/api/users/nok/${nokId}/resend`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert('Invitation resent successfully');
    } catch (err) {
      alert('Failed to resend invitation');
    }
  };

  const getRelationshipLabel = (value) => {
    const labels = {
      spouse: 'Spouse',
      child: 'Child',
      parent: 'Parent',
      sibling: 'Sibling',
      friend: 'Friend',
      lawyer: 'Lawyer',
      advisor: 'Advisor',
      other: 'Other'
    };
    return labels[value] || value;
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Global Next of Kin</h1>
        <div>
          <button onClick={() => navigate('/dashboard')} style={styles.backButton}>
            ← Back to Dashboard
          </button>
        </div>
      </div>

      {/* Plan Info Banner */}
      <div style={styles.planBanner}>
        <div style={styles.planInfo}>
          <span style={styles.planName}>{userPlan?.toUpperCase()} PLAN</span>
          <span style={styles.planLimit}>
            {noks.length} of {planLimits.max} used • Minimum required: {planLimits.min}
          </span>
        </div>
        <div style={styles.progressBar}>
          <div style={{
            ...styles.progressFill,
            width: `${(noks.length / planLimits.max) * 100}%`,
            backgroundColor: noks.length < planLimits.min ? '#ffc107' : '#FFD700'
          }} />
        </div>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      {/* Add Button - Only shows if user can add more NOKs */}
      {!showForm && noks.length < planLimits.max && (
        <div style={styles.addButtonContainer}>
          <button onClick={() => setShowForm(true)} style={styles.addButton}>
            + Add Global Next of Kin
          </button>
        </div>
      )}

      {/* Add Form */}
      {showForm && (
        <div style={styles.formCard}>
          <h2 style={styles.formTitle}>Add Global Next of Kin</h2>
          <form onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Full Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                style={styles.input}
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                style={styles.input}
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="0244XXXXXX"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Relationship</label>
              <select
                name="relationship"
                value={formData.relationship}
                onChange={handleInputChange}
                style={styles.input}
              >
                <option value="spouse">Spouse</option>
                <option value="child">Child</option>
                <option value="parent">Parent</option>
                <option value="sibling">Sibling</option>
                <option value="friend">Friend</option>
                <option value="lawyer">Lawyer</option>
                <option value="advisor">Advisor</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div style={styles.formActions}>
              <button type="submit" style={styles.submitButton} disabled={submitting}>
                {submitting ? 'Adding...' : 'Add Next of Kin'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} style={styles.cancelButton}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* NOK List */}
      {loading ? (
        <div style={styles.loading}>Loading...</div>
      ) : (
        <div style={styles.nokList}>
          <h2 style={styles.sectionTitle}>Your Global Next of Kin</h2>
          
          {noks.length === 0 ? (
            <div style={styles.emptyState}>
              <img src="/images/nok/nok-illustration.png" alt="No Next of Kin" style={styles.emptyImage} />
              <p>No Global Next of Kin added yet.</p>
              <p style={styles.requiredNote}>Your {userPlan} plan requires at least {planLimits.min} Global Next of Kin.</p>
              <button onClick={() => setShowForm(true)} style={styles.emptyButton}>
                Add Your First Next of Kin
              </button>
            </div>
          ) : (
            noks.map((nok) => (
              <div key={nok._id} style={styles.nokCard}>
                <div style={styles.nokHeader}>
                  <div style={styles.nokInfo}>
                    <img src="/images/nok/nok-avatar.png" alt="" style={styles.nokAvatar} />
                    <div>
                      <span style={styles.nokName}>{nok.name}</span>
                      <span style={styles.nokEmail}>{nok.email}</span>
                    </div>
                  </div>
                  <span style={{
                    ...styles.nokStatus,
                    backgroundColor: nok.isActive ? '#d1fae5' : '#fee2e2',
                    color: nok.isActive ? '#065f46' : '#dc2626'
                  }}>
                    {nok.isActive ? 'Active' : 'Pending'}
                  </span>
                </div>
                
                <div style={styles.nokDetails}>
                  {nok.phone && <p>📞 {nok.phone}</p>}
                  <p>🔗 {getRelationshipLabel(nok.relationship)}</p>
                  <p>📅 Added: {new Date(nok.invitedAt).toLocaleDateString()}</p>
                </div>
                
                <div style={styles.nokActions}>
                  {!nok.isActive && (
                    <button
                      onClick={() => handleResendInvite(nok._id)}
                      style={styles.resendButton}
                    >
                      Resend Invite
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(nok._id)}
                    style={styles.deleteButton}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Info Box */}
      <div style={styles.infoBox}>
        <h3 style={styles.infoTitle}>What are Global Next of Kin?</h3>
        <p style={styles.infoText}>
          Global Next of Kin are people who will have access to ALL your assets by default 
          when you pass away. They are your primary beneficiaries.
        </p>
        <p style={styles.infoText}>
          You can assign specific assets to individual Next of Kin later when creating each asset.
        </p>
      </div>
    </div>
  );
};

// ========================================
// Styles
// ========================================
const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#F5F0E6',
    padding: '20px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    maxWidth: '800px',
    margin: '0 auto 30px'
  },
  title: {
    fontSize: '28px',
    color: '#1A1A1A',
    margin: 0
  },
  backButton: {
    padding: '10px 20px',
    backgroundColor: '#1A1A1A',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer'
  },
  planBanner: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '10px',
    marginBottom: '30px',
    maxWidth: '800px',
    margin: '0 auto 30px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  planInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  planName: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#FFD700'
  },
  planLimit: {
    fontSize: '14px',
    color: '#666'
  },
  progressBar: {
    height: '8px',
    backgroundColor: '#ddd',
    borderRadius: '4px',
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    transition: 'width 0.3s ease'
  },
  errorBox: {
    backgroundColor: '#fee2e2',
    border: '1px solid #ef4444',
    color: '#dc2626',
    padding: '10px',
    borderRadius: '5px',
    marginBottom: '20px',
    maxWidth: '800px',
    margin: '0 auto 20px'
  },
  addButtonContainer: {
    maxWidth: '800px',
    margin: '0 auto 20px',
    textAlign: 'right'
  },
  addButton: {
    padding: '10px 20px',
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },
  formCard: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '10px',
    maxWidth: '500px',
    margin: '0 auto 30px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
  },
  formTitle: {
    fontSize: '18px',
    color: '#FFD700',
    marginBottom: '20px'
  },
  formGroup: {
    marginBottom: '15px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#1A1A1A',
    marginBottom: '5px'
  },
  input: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    fontSize: '14px'
  },
  formActions: {
    display: 'flex',
    gap: '10px',
    marginTop: '20px'
  },
  submitButton: {
    flex: '1',
    padding: '10px',
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: 'transparent',
    color: '#666',
    border: '1px solid #666',
    borderRadius: '5px',
    cursor: 'pointer'
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#666',
    maxWidth: '800px',
    margin: '0 auto'
  },
  nokList: {
    maxWidth: '800px',
    margin: '0 auto'
  },
  sectionTitle: {
    fontSize: '20px',
    color: '#2E7D32',
    marginBottom: '20px'
  },
  emptyState: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '10px',
    textAlign: 'center',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  emptyImage: {
    width: '150px',
    height: 'auto',
    marginBottom: '20px',
    opacity: 0.6
  },
  requiredNote: {
    color: '#666',
    fontSize: '14px',
    marginBottom: '20px'
  },
  emptyButton: {
    padding: '12px 30px',
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    border: 'none',
    borderRadius: '5px',
    fontSize: '16px',
    cursor: 'pointer'
  },
  nokCard: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '10px',
    marginBottom: '15px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  nokHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px'
  },
  nokInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  nokAvatar: {
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    backgroundColor: '#F5F0E6',
    padding: '5px'
  },
  nokName: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#1A1A1A',
    display: 'block',
    marginBottom: '4px'
  },
  nokEmail: {
    fontSize: '12px',
    color: '#666',
    display: 'block'
  },
  nokStatus: {
    padding: '4px 8px',
    borderRadius: '3px',
    fontSize: '12px'
  },
  nokDetails: {
    marginBottom: '15px',
    paddingLeft: '65px',
    fontSize: '14px',
    color: '#666'
  },
  nokActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
    borderTop: '1px solid #eee',
    paddingTop: '15px'
  },
  resendButton: {
    padding: '5px 10px',
    backgroundColor: '#2E7D32',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '12px'
  },
  deleteButton: {
    padding: '5px 10px',
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '12px'
  },
  infoBox: {
    backgroundColor: '#e8f4fd',
    border: '1px solid #2E7D32',
    borderRadius: '10px',
    padding: '20px',
    maxWidth: '800px',
    margin: '30px auto 0'
  },
  infoTitle: {
    fontSize: '16px',
    color: '#1A1A1A',
    marginBottom: '10px'
  },
  infoText: {
    fontSize: '14px',
    color: '#1A1A1A',
    marginBottom: '10px',
    lineHeight: '1.5'
  }
};

export default NextOfKin;